import React, { useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { ReactReader } from 'react-reader';
import OceanBackground from './components/OceanBackground';
import {
  fetchSampleEpub,
  fetchSubscriptionStatus,
  subscribeToMailerLite,
} from './components/mailerLite';

const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL
  || (process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : '');

async function trackVisit() {
  await fetch(`${BACKEND_BASE_URL}/api/track-visit`, {
    method: 'POST',
    credentials: 'include',
  });
}

const BOOK_PREVIEW = {
  hsmb: {
    name: 'His Shell, My Blade',
    chapters: '1-3',
  },
  coa: {
    name: 'The Consort of Ashes',
    chapters: '1-3',
  },
  sv: {
    name: 'Starlight Verdict',
    chapters: '1-13',
  },
};

function Home() {
  const [activeBookId, setActiveBookId] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loadingSampleId, setLoadingSampleId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [readerBookName, setReaderBookName] = useState('');
  const [readerUrl, setReaderUrl] = useState('');
  const [readerLocation, setReaderLocation] = useState(null);
  const isModalOpen = Boolean(activeBookId);
  const activeBook = BOOK_PREVIEW[activeBookId];
  const isReaderOpen = Boolean(readerUrl);

  useEffect(() => {
    trackVisit().catch(() => {
      // Ignore tracking failures; this should never block page load.
    });

    const hasClientCookie = document.cookie
      .split(';')
      .some((cookie) => cookie.trim() === 'is_subscribed=true');
    if (hasClientCookie) {
      setIsSubscribed(true);
    }

    let isMounted = true;
    fetchSubscriptionStatus()
      .then((result) => {
        if (isMounted) {
          setIsSubscribed(Boolean(result?.isSubscribed));
        }
      })
      .catch(() => {
        // Keep client-side state as a fallback when status check fails.
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => () => {
    if (readerUrl) {
      URL.revokeObjectURL(readerUrl);
    }
  }, [readerUrl]);

  const openModal = (bookId) => (e) => {
    e.stopPropagation();
    setActiveBookId(bookId);
    setEmail('');
    setStatus('idle');
    setErrorMessage('');
  };

  const closeModal = () => {
    setActiveBookId('');
  };

  const closeReader = () => {
    if (readerUrl) {
      URL.revokeObjectURL(readerUrl);
    }
    setReaderUrl('');
    setReaderBookName('');
    setReaderLocation(null);
  };

  const openSample = async (bookId) => {
    if (!bookId) return;

    setLoadingSampleId(bookId);
    setErrorMessage('');
    try {
      const blob = await fetchSampleEpub(bookId);
      const sampleUrl = URL.createObjectURL(blob);
      if (readerUrl) {
        URL.revokeObjectURL(readerUrl);
      }
      setReaderBookName(BOOK_PREVIEW[bookId]?.name || 'Book Sample');
      setReaderUrl(sampleUrl);
      setReaderLocation(null);
      closeModal();
    } catch (error) {
      setErrorMessage(error.message || 'Could not load sample.');
      setStatus('error');
    } finally {
      setLoadingSampleId('');
    }
  };

  const handleSampleButton = (bookId) => async (e) => {
    e.stopPropagation();
    if (!isSubscribed) {
      openModal(bookId)(e);
      return;
    }
    await openSample(bookId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setErrorMessage('Email is required.');
      setStatus('error');
      return;
    }

    if (!activeBookId) {
      setErrorMessage('Missing book selection.');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      await subscribeToMailerLite(trimmedEmail, activeBookId);
      document.cookie = 'is_subscribed=true; Path=/; Max-Age=31536000; SameSite=Lax';
      setIsSubscribed(true);
      setStatus('success');
      await openSample(activeBookId);
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.message || 'Subscription failed.');
    }
  };

  return (
    <>
      <main>
        <section className="hero">
          <div className="hero-logo">
            <img src="/qm_logo_golden_400x.png" alt="Quill and Myth Logo" />
          </div>
          <p>
            <span>When the quill conjures myth,</span><br />
            <span>destiny rewrites.</span>
          </p>
        </section>
        
        <section className="fictions">
          <h2>Featured Fictions</h2>
          <div className="fiction-list">
            <div
              className="fiction-card"
              onClick={(e) => {
                if (e.target.closest('button')) return;
                window.location.href = "https://www.amazon.com/His-Shell-My-Blade-Ninn-ebook/dp/B0GBHCNHKG";
              }}
              style={{ cursor: 'pointer' }}
            >
              <img src="/hsmb_cover_medium.jpg" alt="His Shell, My Blade" />
              <h3>His Shell, My Blade</h3>
              <p>
                He called labor “easy.” Now he has to survive it.
              </p>
              <div className="book-keywords" aria-label="Keywords for His Shell, My Blade">
                <span className="keyword-tag">#psychologicalthriller</span>
                <span className="keyword-tag">#revenge</span>
                <span className="keyword-tag">#bodyswap</span>
                <span className="keyword-tag">#bettrayal</span>
              </div>
              <div className="fiction-actions">
                <button
                  className="sample-button"
                  onClick={handleSampleButton('hsmb')}
                  disabled={loadingSampleId === 'hsmb'}
                >
                  {loadingSampleId === 'hsmb'
                    ? 'Loading sample...'
                    : isSubscribed
                      ? 'Read sample'
                      : 'Free sample'}
                </button>
                <button
                  className="buy-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = "https://www.amazon.com/His-Shell-My-Blade-Ninn-ebook/dp/B0GBHCNHKG";
                  }}
                >
                  Read @amazon
                </button>
              </div>
            </div>
            <div className="fiction-card">
              <img src="/sv_cover_medium.jpg" alt="Starlight Verdict" />
              <h3>Starlight Verdict</h3>
              <p>
                If God gave you a second chance at life, would you seek forgiveness—or become the devil yourself?
              </p>
              <div className="book-keywords" aria-label="Keywords for Starlight Verdict">
                <span className="keyword-tag">#showbiz</span>
                <span className="keyword-tag">#badassheroine</span>
                <span className="keyword-tag">#revenge</span>
                <span className="keyword-tag">#powercouple</span>
                <span className="keyword-tag">#bettrayal</span>
                <span className="keyword-tag">#rebirth</span>
              </div>
              <div className="fiction-actions">
                <button
                  className="sample-button"
                  onClick={handleSampleButton('sv')}
                  disabled={loadingSampleId === 'sv'}
                >
                  {loadingSampleId === 'sv'
                    ? 'Loading full book...'
                    : isSubscribed
                      ? 'Read full book'
                      : 'Free full book'}
                </button>
                <button
                  className="buy-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = "https://www.amazon.com/Starlight-Verdict-Hollywood-Vendetta-Vengeance-ebook/dp/B0GKX1ZJQR";
                  }}
                >
                  Read @amazon
                </button>
              </div>
            </div>
            <div className="fiction-card">
              <img src="/ca_cover_medium.jpg" alt="The Consort of Ashes" />
              <h3>The Consort of Ashes</h3>
              <p>
                Providence says Cassian Riva was destined to rule. Providence is wrong. Only the man who stands with Valeria is destined for the crown.
              </p>
              <div className="book-keywords" aria-label="Keywords for The Consort of Ashes">
                <span className="keyword-tag">#rebirth</span>
                <span className="keyword-tag">#revenge</span>
                <span className="keyword-tag">#court</span>
                <span className="keyword-tag">#intrigue</span>
              </div>
            </div>
          </div>
        </section>

        <section className="author">
          <h2>About the Author</h2>
          <p>I don't write because life is epic—I write because most days, it isn't. As someone who navigates the rigid logic of informatics by day and the beautiful chaos of motherhood by night, I know how mundane and unforgiving the world can be.</p>
          <p>Real life rarely hands out second chances, perfect comebacks, or the exact line you wish you'd said years ago on the stairs, in that room, to that person. So I give my characters what we don't always get: a rewind button, a sharper voice, and the kind of "finally" that lands clean.</p>
          <p>If my stories make you smile, exhale, or quietly whisper "good"—then they've done what I hoped they would do. Sometimes fiction isn't about being realistic; sometimes it's about being satisfying.</p>
          <p>Contact me: <a href="mailto:quillmyth0@gmail.com">quillmyth0@gmail.com</a></p>
        </section>
      </main>
      <footer className="footer">
        <p>&copy; 2025 Quill and Myth. All Rights Reserved.</p>
      </footer>

      {isModalOpen && activeBook && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="modal-close"
              onClick={closeModal}
              aria-label="Close"
            >
              &times;
            </button>
            <h3 className="modal-title">Unlock Free Chapters!</h3>
            <p className="modal-copy">
              Read Chapters <span className="modal-highlight">{activeBook.chapters}</span> of{' '} 
              <span className="modal-highlight">{activeBook.name}</span>{' '}
              immediately when you join for free! Plus, get exclusive sneak peeks,
              secret updates, and launch alerts you won't find anywhere else.
            </p>
            <form className="modal-form" onSubmit={handleSubmit}>
              <input
                className="modal-input"
                type="email"
                name="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={status === 'success'}
              />
              <p className="modal-disclaimer">You can unsubscribe anytime.</p>
              <button
                type="submit"
                className="sample-button modal-submit"
                disabled={status === 'loading' || status === 'success'}
              >
                {status === 'loading'
                  ? 'Submitting...'
                  : status === 'success'
                    ? 'Sent'
                    : 'Read now'}
              </button>
            </form>
            {status === 'success' && (
              <div className="modal-result modal-result-success">
                <h4>Thank you!</h4>
                <p>You have successfully joined our subscriber list.</p>
              </div>
            )}
            {status === 'error' && (
              <div className="modal-result modal-result-error">
                <h4>Something went wrong,</h4>
                <p>{errorMessage || 'Please try again.'}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {isReaderOpen && (
        <div className="reader-overlay" onClick={closeReader}>
          <div className="reader-shell" onClick={(e) => e.stopPropagation()}>
            <div className="reader-toolbar">
              <h3 className="reader-title">{readerBookName}</h3>
              <button
                type="button"
                className="reader-close"
                onClick={closeReader}
                aria-label="Close reader"
              >
                Close
              </button>
            </div>
            <div className="reader-body">
              <ReactReader
                url={readerUrl}
                epubInitOptions={{ openAs: 'epub' }}
                location={readerLocation}
                locationChanged={(epubcfi) => setReaderLocation(epubcfi)}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <OceanBackground />
      <Routes>
        <Route path="/" element={<Home />} />
      </Routes>
    </div>
  );
}

export default App;
